import { useState, type FormEvent } from 'react';
import { motion } from 'framer-motion';
import { Check, Sparkles, ArrowRight, CalendarDays } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { B2BPlatformCenter } from '@modules/public/landing/B2BPlatformCenter';
import { B2BMarketingPageLayout } from './_shared/B2BMarketingPageLayout';
import { FeaturesPageShowcase } from './FeaturesPageShowcase';

/* ─── Hero gradient — brand primary violet ─────────────────────────────────── */
const HERO_GRADIENT =
  'linear-gradient(160deg, #5c54d8 0%, #6e66e8 28%, #8E86FF 52%, #9b94ff 72%, #c8c4ff 100%)';

/* ─── Hero section ──────────────────────────────────────────────────────────── */

function FeaturesHeroSection() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const t = email.trim();
    navigate(t ? `/business/demo?email=${encodeURIComponent(t)}` : '/business/demo');
  };

  return (
    <div className="relative">
      {/* ── Gradient hero card ──────────────────────────────────────────────── */}
      <section
        className="relative mx-2 mt-2 overflow-visible rounded-2xl sm:mx-4 sm:rounded-3xl lg:mx-6 lg:rounded-[2rem]"
        style={{ background: HERO_GRADIENT, paddingBottom: '14rem' }}
        aria-label="Features hero"
      >
        {/* Decorative blurs */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-[inherit]" aria-hidden>
          <div className="absolute -left-[25%] -top-[20%] h-[85%] w-[75%] rounded-full opacity-[0.15] blur-3xl"
            style={{ background: '#4a42a8' }} />
          <div className="absolute -right-[10%] top-[30%] h-[60%] w-[55%] rounded-full opacity-[0.18] blur-3xl"
            style={{ background: '#6B63D4' }} />
          <svg className="absolute bottom-0 left-0 h-28 w-full text-white/[0.09]"
            viewBox="0 0 1440 112" preserveAspectRatio="none" aria-hidden>
            <path fill="currentColor" d="M0,80 C480,112 960,16 1440,48 L1440,112 L0,112 Z" />
          </svg>
        </div>

        {/* Hero text */}
        <div className="relative z-10 mx-auto max-w-2xl px-6 pt-12 text-center md:pt-16 lg:pt-20">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 text-sm font-medium text-white backdrop-blur-sm">
              <Sparkles className="h-4 w-4" aria-hidden />
              Platform Features
            </div>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 26 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.08 }}
            className="mb-4 text-3xl font-extrabold leading-tight tracking-tight text-white sm:text-4xl md:text-[3rem] md:leading-[1.15]"
          >
            Everything you need
            <br />
            <span className="text-white/80">to hire smarter</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.14 }}
            className="mx-auto mb-7 max-w-xl text-base leading-relaxed text-white/88 md:text-lg"
          >
            AI interviews, explainable role-fit scoring, visual workflow engine, and pipeline
            management — one platform for agencies and in-house teams.
          </motion.p>

          {/* ── Email form + Book demo button ─────────────────────────────── */}
          <motion.div
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.18 }}
            className="mb-5 flex flex-col items-center gap-3 px-2 sm:flex-row sm:justify-center"
          >
            {/* Email CTA — pill style */}
            <form
              onSubmit={handleSubmit}
              className="flex w-full max-w-sm overflow-hidden rounded-2xl bg-white shadow-[0_4px_32px_rgba(0,0,0,0.22)] sm:w-auto"
            >
              <input
                type="email"
                name="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your work email"
                className="w-full bg-transparent px-5 py-3.5 text-[15px] text-gray-900 placeholder:text-gray-400 focus:outline-none sm:w-52 md:w-64"
              />
              <button
                type="submit"
                className="m-1.5 shrink-0 rounded-xl bg-gray-900 px-5 py-2.5 text-[14px] font-semibold text-white transition-colors hover:bg-gray-700"
              >
                Start now
              </button>
            </form>

            {/* Secondary: Book a demo — sits beside the form */}
            <Link
              to="/business/demo"
              className="inline-flex shrink-0 items-center gap-2 rounded-2xl bg-white/15 px-5 py-[0.82rem] text-[14px] font-semibold text-white ring-1 ring-white/25 backdrop-blur-sm transition-colors hover:bg-white/25"
            >
              <CalendarDays className="h-4 w-4 shrink-0" aria-hidden />
              Book a demo
              <ArrowRight className="h-3.5 w-3.5 shrink-0" aria-hidden />
            </Link>
          </motion.div>

          {/* Trust badges */}
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            transition={{ duration: 0.45, delay: 0.24 }}
            className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-white/82"
          >
            {['No credit card required', 'GDPR compliant'].map((t) => (
              <div key={t} className="flex items-center gap-1.5">
                <Check className="h-4 w-4 shrink-0" strokeWidth={2.5} aria-hidden />
                {t}
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── Product showcase ─────────────────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: 32 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.28 }}
        className="relative z-20 mx-auto -mt-[14rem] w-[calc(100%-2rem)] max-w-5xl select-none sm:w-[calc(100%-3.5rem)] md:w-[calc(100%-5rem)] lg:w-[calc(100%-7rem)]"
        style={{ pointerEvents: 'none' }}
      >
        <FeaturesPageShowcase />
      </motion.div>
    </div>
  );
}

/* ─── CTA section ───────────────────────────────────────────────────────────── */

function FeaturesCTASection() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    navigate(trimmed ? `/business/demo?email=${encodeURIComponent(trimmed)}` : '/business/demo');
  };

  return (
    <section className="px-2 py-12 sm:px-4 md:py-16 lg:py-20">
      <div className="relative mx-auto w-full max-w-[1600px] overflow-hidden rounded-2xl bg-[#8E86FF] md:rounded-3xl">
        {/* Background depth shapes */}
        <div className="pointer-events-none absolute inset-0" aria-hidden>
          <div className="absolute -left-16 -top-16 h-52 w-52 rounded-full bg-[#6d64e8]/50 blur-3xl" />
          <div className="absolute -bottom-20 right-0 h-64 w-64 rounded-full bg-[#7B6EE8]/60 blur-3xl" />
          <div className="absolute left-1/2 top-1/2 h-40 w-40 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/[0.06] blur-2xl" />
          <svg className="absolute left-0 top-0 h-16 w-full text-white/[0.06]"
            viewBox="0 0 1440 64" preserveAspectRatio="none" aria-hidden>
            <path fill="currentColor" d="M0,32 C360,64 720,0 1080,32 C1260,48 1380,16 1440,8 L1440,0 L0,0 Z" />
          </svg>
        </div>

        {/* Illustration placeholders */}
        <div className="pointer-events-none absolute bottom-5 left-6 hidden h-28 w-28 items-center justify-center rounded-2xl border-2 border-dashed border-white/25 text-center md:flex" aria-hidden>
          <span className="text-[9px] font-semibold uppercase leading-snug tracking-wide text-white/40">Illustration<br />placeholder</span>
        </div>
        <div className="pointer-events-none absolute right-6 top-5 hidden h-28 w-28 items-center justify-center rounded-2xl border-2 border-dashed border-white/25 text-center md:flex" aria-hidden>
          <span className="text-[9px] font-semibold uppercase leading-snug tracking-wide text-white/40">Illustration<br />placeholder</span>
        </div>

        {/* CTA body */}
        <div className="relative z-10 flex flex-col items-center px-8 py-14 text-center md:py-16 lg:py-20">
          <motion.p
            initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="mb-3 text-[11px] font-bold uppercase tracking-[0.22em] text-white/60"
          >
            AI-powered hiring, end to end
          </motion.p>

          <motion.h2
            initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ delay: 0.06 }}
            className="mb-4 max-w-xl text-2xl font-extrabold leading-tight text-white sm:text-3xl md:text-[2.5rem] md:leading-[1.18]"
          >
            Reduce Time-to-Hire.
            <br />
            Start for Free Today.
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="mb-8 max-w-md text-base leading-relaxed text-white/82"
          >
            Set up your workspace, post your first role, and run AI-screened interviews — all
            within your free trial. No credit card required.
          </motion.p>

          <motion.form
            initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ delay: 0.14 }}
            onSubmit={handleSubmit}
            className="flex w-full max-w-md flex-col gap-3 sm:flex-row"
          >
            <input
              type="email" name="email" autoComplete="email"
              value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your work email"
              className="flex-1 rounded-xl border-0 bg-white px-5 py-3.5 text-[15px] text-gray-900 shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-white/60"
            />
            <button type="submit"
              className="flex items-center justify-center gap-2 rounded-xl bg-gray-900 px-5 py-3.5 text-[15px] font-semibold text-white shadow-md transition-colors hover:bg-gray-800"
            >
              Start now
              <ArrowRight className="h-4 w-4 shrink-0" aria-hidden />
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
}

/* ─── Page ──────────────────────────────────────────────────────────────────── */

export function FeaturesPage() {
  return (
    <B2BMarketingPageLayout>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}
        className="text-gray-900 dark:text-white"
      >
        <FeaturesHeroSection />

        <div className="mt-10 md:mt-14">
          <B2BPlatformCenter />
        </div>

        <FeaturesCTASection />
      </motion.div>
    </B2BMarketingPageLayout>
  );
}
